// Wannier functions (sub-module of Tight Binding): the project's memory, the recipe the steps build, the mesh
// suggestion, the projection choices, the calls to /api/wannier and the figures (band plot with the windows, model
// against DFT, decay of the hoppings). Server: products/wannier.py, api/wannier.py; engine: docs/WANNIER.md.
import { get, patch, post, type Job, type Project } from "./api";
import type { QeIssue } from "./qe";

export type WannierSource = "dft" | "files" | "model";
export type WSpin = "none" | "collinear" | "spinor";

export interface Windows { outer: [number, number]; frozen?: [number, number] }

/** The project's memory (project.settings.wannier). */
export interface WannierMemory {
  source: WannierSource;
  dft_run?: string;                    // route A: the DFT run's job id
  dft_label?: string;
  seed: string;
  mesh: [number, number, number];
  num_bands?: number | null;           // null: = num_wann
  exclude_bands?: string;              // "1-5"
  projections: Record<string, string[]>;
  expert?: string;                     // Wannier90 projection lines, one per line (then num_wann is given)
  num_wann?: number | null;            // with expert lines
  windows?: Windows | null;            // null: isolated bands
  pd?: { min: number; max: number; frozen: boolean } | null;   // projectability disentanglement (W4b); frozen: also
                                       // freeze the frozen energy window
  scdm?: { entanglement: "isolated" | "erfc" | "gaussian"; mu: number; sigma: number } | null;   // SCDM projections
  iterations: { disentangle: number; localise: number };
  // route B
  files?: { win?: string; amn?: string; mmn?: string; eig?: string; spn?: string };   // spn: spinors only (optional)
  files_dn?: { win?: string; amn?: string; mmn?: string; eig?: string };
  spin?: WSpin;
  efermi?: number | null;
  // route C
  model?: { hr?: string; hr_dn?: string; wout?: string; win?: string; centres?: string; wsvec?: string; tb?: string };
  hop_cutoff?: number;
  job?: string;                        // the run shown
  from_example?: { id: string; try: string; title: string; about: string };   // a "try this" of an illustrative project
  teach?: boolean;                     // the teaching notes are shown
}

export const defaultWannierMemory = (seed = "wannier"): WannierMemory => ({
  source: "dft", seed, mesh: [4, 4, 4], num_bands: null, projections: {}, windows: null,
  iterations: { disentangle: 500, localise: 200 }, spin: "none", efermi: null, hop_cutoff: 0,
});

export const WANNIER_KINDS = ["wannier.qe", "wannier.w90", "wannier.restart", "wannier.model"];

// == projections ======================================================================================================
/** Per orbital family: the Wannier90 names offered (families first, then single orbitals and hybrids). */
export const PROJECTION_GROUPS: { label: string; names: string[] }[] = [
  { label: "Shells", names: ["s", "p", "d", "f"] },
  { label: "p orbitals", names: ["pz", "px", "py"] },
  { label: "d orbitals", names: ["dz2", "dxz", "dyz", "dx2-y2", "dxy"] },
  { label: "Hybrids", names: ["sp", "sp2", "sp3", "sp3d", "sp3d2"] },
];
export const PROJECTION_SIZE: Record<string, number> = {
  s: 1, p: 3, d: 5, f: 7, pz: 1, px: 1, py: 1, dz2: 1, dxz: 1, dyz: 1, "dx2-y2": 1, dxy: 1,
  sp: 2, sp2: 3, sp3: 4, sp3d: 5, sp3d2: 6,
};

/** Wannier functions from the projections and the atoms of each element (x 2 with spinors). */
export function countWann(projections: Record<string, string[]>, counts: Record<string, number>, spin: WSpin = "none"): number {
  let n = 0;
  for (const [el, names] of Object.entries(projections))
    for (const o of names) n += (PROJECTION_SIZE[o] ?? 0) * (counts[el] ?? 0);
  return spin === "spinor" ? 2 * n : n;
}

/** SKFitter's default orbitals ("s", "p", "d", "s*") as Wannier90 projections (s* has no atomic counterpart). */
export function projectionsFromDefaults(elements: { symbol: string; default_orbitals: string[] }[]): Record<string, string[]> {
  return Object.fromEntries(elements.map((e) => [e.symbol, e.default_orbitals.filter((o) => o in PROJECTION_SIZE)]));
}

// == the mesh ==========================================================================================================
const norm3 = (v: number[]) => Math.hypot(v[0], v[1], v[2]);
function cross(a: number[], b: number[]) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }

/** |b_i| (1/Å, with 2π) of a lattice (rows a1, a2, a3 in Å). */
export function reciprocalLengths(lattice: number[][]): number[] {
  const [a1, a2, a3] = lattice;
  const vol = Math.abs(a1[0] * (a2[1] * a3[2] - a2[2] * a3[1]) - a1[1] * (a2[0] * a3[2] - a2[2] * a3[0]) + a1[2] * (a2[0] * a3[1] - a2[1] * a3[0]));
  return [cross(a2, a3), cross(a3, a1), cross(a1, a2)].map((c) => (2 * Math.PI * norm3(c)) / vol);
}

/** Axes along which the atoms leave a vacuum gap of more than `gap` Å (a layer or a wire): one k-point there. */
export function vacuumAxes(lattice: number[][], frac: number[][], gap = 8): boolean[] {
  return [0, 1, 2].map((i) => {
    const len = norm3(lattice[i]);
    const xs = frac.map((f) => ((f[i] % 1) + 1) % 1).sort((a, b) => a - b);
    if (!xs.length) return false;
    let widest = 1 - xs[xs.length - 1] + xs[0];
    for (let j = 1; j < xs.length; j++) widest = Math.max(widest, xs[j] - xs[j - 1]);
    return widest * len > gap;
  });
}

/** A uniform mesh with about `spacing` (1/Å, 2π included) between k-points; 1 along vacuum. Quick: 0.55, accurate: 0.27 (silicon: 4×4×4, 8×8×8). */
export function suggestMesh(lattice: number[][], frac: number[][], spacing: number): [number, number, number] {
  const b = reciprocalLengths(lattice), vac = vacuumAxes(lattice, frac);
  return b.map((bi, i) => (vac[i] ? 1 : Math.max(2, Math.ceil(bi / spacing - 1e-9)))) as [number, number, number];
}

// == the recipe =======================================================================================================
/** The prayog-wannier-recipe of route A (from a DFT run) as the steps have set it. */
export function buildRecipe(m: WannierMemory): Record<string, unknown> {
  const expert = (m.expert ?? "").split("\n").map((l) => l.trim()).filter(Boolean);
  const projections: Record<string, string[]> = Object.fromEntries(Object.entries(m.projections).filter(([, v]) => v.length));
  if (expert.length) projections.expert = expert;
  const r: Record<string, unknown> = {
    format: "prayog-wannier-recipe", version: 1, seed: m.seed || "wannier", source: { dft_run: m.dft_run },
    mesh: m.mesh, projections, iterations: m.iterations,
  };
  if (m.scdm) {                                           // SCDM makes its own projections; num_wann is given
    delete r.projections;
    r.scdm = m.scdm.entanglement === "isolated" ? { entanglement: "isolated" } : m.scdm;
    if (m.num_wann) r.num_wann = m.num_wann;
  }
  if (expert.length && m.num_wann) r.num_wann = m.num_wann;
  if (m.num_bands) r.num_bands = m.num_bands;
  if ((m.exclude_bands ?? "").trim()) r.exclude_bands = m.exclude_bands!.trim();
  if (m.pd && !m.scdm) {
    r.windows = { projectability: { min: m.pd.min, max: m.pd.max },
      ...(m.pd.frozen && m.windows?.frozen ? { frozen: m.windows.frozen } : {}) };
  } else if (m.windows && !(m.scdm?.entanglement === "isolated")) r.windows = m.windows.frozen ? m.windows : { outer: m.windows.outer };
  return r;
}

/** What still stops a route-A run (shown before the check comes back). */
export function recipeProblems(m: WannierMemory): string[] {
  const p: string[] = [];
  if (!m.dft_run) p.push("Choose a DFT run.");
  if (m.scdm) { if (!m.num_wann) p.push("Give num_wann for SCDM."); }
  else if (!Object.values(m.projections).some((v) => v.length) && !(m.expert ?? "").trim()) p.push("Choose the projections.");
  if (m.pd && !(m.pd.min < m.pd.max)) p.push("Projectability: min must be below max.");
  if (m.mesh.some((n) => !Number.isInteger(n) || n < 1)) p.push("The mesh needs whole numbers ≥ 1.");
  if (m.windows && !m.pd && m.windows.outer[0] >= m.windows.outer[1]) p.push("The outer window's bottom must be below its top.");
  if (!m.pd && m.windows?.frozen && (m.windows.frozen[0] >= m.windows.frozen[1]
      || m.windows.frozen[0] < m.windows.outer[0] - 1e-9 || m.windows.frozen[1] > m.windows.outer[1] + 1e-9))
    p.push("The frozen window must lie inside the outer one.");
  return p;
}

export const seedFromName = (s: string) => (s.toLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "wannier");

// == the API ==========================================================================================================
export interface WannierStatus {
  available: boolean; qe_available: boolean; route_a_allowed: boolean;
  limits: Record<string, { value: number | boolean | null; label: string; unit: string }>;
  density_retention_days: number | null; budget: { hours: number; used: number; left: number };
  queue: { running: number; queued: number }; my_jobs: { id: string; kind: string; status: string; position: number | null }[];
}
export interface WannierCheck {
  issues: QeIssue[]; ok: boolean; num_wann: number | null; num_bands: number | null; kpoints: number | null;
  efermi_eV: number | null; dft_run: string | null; win: string | null;
}
export interface WannierWindows {
  dft_run: string; efermi_eV: number | null; windows: Windows | null; message?: string;
  x: number[] | null; ticks: number[] | null; tick_labels: string[] | null;
  channels: Record<string, { windows: Windows | null; projectability: number[][]; energies: number[][]; count: number }>;
}
export interface WannierRunItem {
  job_id: string; kind: string; status: string; route: string | null; seed: string | null; spin: string | null;
  num_wann: number | null; project_id: string | null; project_name: string | null; created_at: string | null;
  error: string | null; omega: Record<string, number> | null; band_error_max: number | null;
  converged: Record<string, { localisation: boolean; disentanglement: boolean | null }> | null; warnings: number;
  model_file: string | null; bands_file: string | null; zip_file: string | null; dft_run: string | null;
  restarted_from: string | null; efermi_eV: number | null;
}
export interface BandError { max: number | null; rms: number | null; states: number; window?: number[] }
export interface WannierChannel {
  history?: { disentanglement: (number | null)[][]; localisation: (number | null)[][] };
  structure?: { lattice: number[][]; species: string[]; cart: number[][] } | null;
  spreads: { each: number[]; Total?: number; I?: number; D?: number; OD?: number } | null;
  centres: number[][]; centre_offsets: number[] | null; shortest_bond: number | null;
  decay: { distance: number[] | null; max_hopping: number[] | null; cutoff: number | null; range_for_cutoff: number | null;
    edge_hopping: number | null };
  imag_fraction: number | null; band_error_path: BandError | null; band_error_mesh: BandError | null;
  // spinor models (W4e): are the DFT's Kramers pairs kept degenerate by the model (along the path or between mesh points)?
  spin_degeneracy?: { expected: boolean; dft_max_split: number | null; max_split?: number; rms_split?: number;
    max_split_window?: number | null; where?: string; worst_k?: number[] } | null;
  converged: { localisation: boolean; disentanglement: boolean | null } | null;
  warnings: { code: string; message: string }[] | null;
}
export interface WannierReport extends WannierRunItem {
  wannier90_version: string | null; times_s: Record<string, number> | null; total_s: number | null; notes: string[];
  all_warnings: string[]; channels: Record<string, WannierChannel>; files: Record<string, string>;
  density_reused?: boolean | null;
}
export interface WannierBands {
  efermi_eV: number; spin: string; seeds: string[]; channels: Record<string, string | null>; windows: Windows | null;
  dft: { x: number[]; ticks: [number, string][]; lsda: boolean; energies?: number[][]; up?: number[][]; down?: number[][];
    // route B: the .eig's states at the mesh k-points on the path, per seed; the path from the .win or the structure
    points?: Record<string, { x: number[]; energies: number[][] }>; source?: "win" | "structure" };
  model: Record<string, number[][]>;
}

export const wannierStatus = () => get<WannierStatus>("/wannier");
export const wannierCheck = (recipe: Record<string, unknown>, projectId?: string) =>
  post<WannierCheck>("/wannier/check", { recipe, project_id: projectId });
export const wannierWindows = (dftRun: string, projections: Record<string, string[]>, projectId?: string) =>
  post<WannierWindows>("/wannier/windows", { dft_run: dftRun, projections, project_id: projectId });
export const wannierRuns = () => get<WannierRunItem[]>("/wannier/runs");
export const wannierReport = (jobId: string) => get<WannierReport>(`/wannier/runs/${jobId}/report`);
export const wannierBands = (jobId: string) => get<WannierBands>(`/wannier/runs/${jobId}/bands`);
export const submitWannier = (projectId: string, kind: string, params: Record<string, unknown>) =>
  post<Job>("/jobs", { kind, project_id: projectId, params });

/** The job of each route, from the memory. */
export function runParams(m: WannierMemory): { kind: string; params: Record<string, unknown> } {
  if (m.source === "files") {
    const { spn, ...rest } = m.files ?? {};
    const files = m.spin === "spinor" && spn ? { ...rest, spn } : rest;
    const p: Record<string, unknown> = { files, spin: m.spin ?? "none", efermi: m.efermi ?? 0, seed: m.seed || "wannier" };
    if (m.spin === "collinear") p.files_dn = m.files_dn;
    return { kind: "wannier.w90", params: p };
  }
  if (m.source === "model") {
    const md = m.model ?? {};
    const p: Record<string, unknown> = { seed: m.seed || "wannier", spin: m.spin ?? "none", hop_cutoff: m.hop_cutoff ?? 0 };
    for (const [k, v] of Object.entries(md)) if (v) p[k] = v;
    if (m.efermi != null) p.efermi = m.efermi;
    return { kind: "wannier.model", params: p };
  }
  return { kind: "wannier.qe", params: { recipe: buildRecipe(m) } };
}

/** What still stops route B or C. */
export function filesProblems(m: WannierMemory): string[] {
  const p: string[] = [];
  if (m.source === "files") {
    for (const [group, label] of [["files", ""], ...(m.spin === "collinear" ? [["files_dn", " (spin down)"]] : [])] as [keyof WannierMemory, string][]) {
      const g = (m[group] ?? {}) as Record<string, string | undefined>;
      for (const r of ["win", "amn", "mmn", "eig"]) if (!g[r]) p.push(`Choose the .${r}${label}.`);
    }
    if (m.efermi == null) p.push("Give the Fermi energy of your DFT run (eV).");
  } else if (m.source === "model") {
    const md = m.model ?? {};
    if (!md.hr) p.push("Choose the _hr.dat.");
    if (!md.wout && !(md.win && md.centres)) p.push("Choose the .wout, or the .win and the _centres.xyz.");
    if (m.spin === "collinear" && !md.hr_dn) p.push("Choose the spin-down _hr.dat.");
  }
  return p;
}

// == figures ==========================================================================================================
const AXIS = "#c3c2b7", GRID = "#e1e0d9", INK = "#0b0b0b", ORANGE = "#eb6834", FROZEN = "rgba(42,120,214,0.13)",
  OUTER = "rgba(235,104,52,0.08)";

function windowShapes(w: Windows | null | undefined, editable = false) {
  if (!w) return [];
  const band = (r: [number, number], fill: string, line: string) => ({ type: "rect", xref: "x domain", yref: "y", x0: 0, x1: 1,
    y0: r[0], y1: r[1], fillcolor: fill, line: { color: line, width: editable ? 2 : 1, dash: "dot" }, layer: "below", editable });
  // (the windows of a projectability run may have no outer window)
  return [...(w.outer ? [band(w.outer, OUTER, ORANGE)] : []), ...(w.frozen ? [band(w.frozen, FROZEN, "#2a78d6")] : [])];
}
const kAxis = (x: number[], ticks: number[], labels: string[] | null) => ({
  range: [x[0] ?? 0, x[x.length - 1] ?? 1], tickvals: ticks, ticktext: labels ?? ticks.map(() => ""), showgrid: false,
  zeroline: false, showline: true, linecolor: AXIS });
const nodes = (ticks: number[]) => ticks.map((t) => ({ type: "line", xref: "x", yref: "y domain", x0: t, x1: t, y0: 0, y1: 1,
  layer: "below", line: { color: GRID, width: 1 } }));
const ef = { type: "line", xref: "x domain", yref: "y", x0: 0, x1: 1, y0: 0, y1: 0, layer: "below", line: { color: AXIS, width: 1, dash: "dash" } };

/** Step 4: the DFT bands coloured by the projectability onto the chosen orbitals, with the two windows. */
export function windowsFigure(w: WannierWindows, spin: string, windows: Windows | null | undefined, range: [number, number],
  editable = false) {
  const ch = w.channels[spin];
  const x = w.x ?? [];
  const data: any[] = [];
  if (ch) ch.energies.forEach((band, b) => {
    data.push({ x, y: band, type: "scattergl", mode: "lines", line: { color: "#d0cfc8", width: 1 }, hoverinfo: "skip", showlegend: false });
    data.push({ x, y: band, type: "scattergl", mode: "markers", showlegend: false,
      marker: { size: 4, color: ch.projectability[b], cmin: 0, cmax: 1, colorscale: [[0, "#e6e5df"], [0.5, "#f4a261"], [1, "#c2410c"]],
        showscale: b === 0, colorbar: { title: { text: "projectability", side: "right" }, thickness: 10 } },
      hovertemplate: "%{y:.3f} eV · p = %{marker.color:.2f}<extra></extra>" });
  });
  return {
    data,
    layout: {
      title: { text: "DFT bands and the windows", x: 0, xanchor: "left", font: { size: 14 } },
      xaxis: kAxis(x, w.ticks ?? [], w.tick_labels), yaxis: { range, title: { text: "E − E<sub>F</sub> (eV)" }, zeroline: false, gridcolor: GRID },
      shapes: [...windowShapes(windows, editable), ...nodes(w.ticks ?? []), ef], margin: { l: 60, r: 80, t: 45, b: 40 },
    },
  };
}

/** Results: the Wannier model (orange) on the DFT bands (black) along the DFT path, the frozen window shaded; route B:
 * the DFT states at the mesh k-points that lie on the path (open circles). */
export function modelBandsFigure(b: WannierBands, seed: string) {
  const ch = b.channels[seed];
  const dft = b.dft.lsda ? (ch === "down" ? b.dft.down : b.dft.up) : b.dft.energies;
  const x = b.dft.x;
  const data: any[] = [];
  (dft ?? []).forEach((y, i) => data.push({ x, y, type: "scattergl", mode: "lines", line: { color: INK, width: 1.3 }, name: "DFT",
    legendgroup: "dft", showlegend: i === 0, hovertemplate: "DFT %{y:.4f} eV<extra></extra>" }));
  const pts = b.dft.points?.[seed];
  if (pts?.x.length) {
    const px: number[] = [], py: number[] = [];
    pts.x.forEach((xx, i) => pts.energies[i].forEach((e) => { px.push(xx); py.push(e); }));
    data.push({ x: px, y: py, type: "scattergl", mode: "markers", name: "DFT (mesh k-points)", marker: { color: INK, size: 6, symbol: "circle-open" },
      hovertemplate: "DFT %{y:.4f} eV<extra></extra>" });
  }
  (b.model[seed] ?? []).forEach((y, i) => data.push({ x, y, type: "scattergl", mode: "lines", name: "Wannier model",
    line: { color: ORANGE, width: 1.8, dash: "dash" }, legendgroup: "w", showlegend: i === 0, hovertemplate: "Wannier %{y:.4f} eV<extra></extra>" }));
  let lo = Infinity, hi = -Infinity;
  for (const y of b.model[seed] ?? []) for (const v of y) { lo = Math.min(lo, v); hi = Math.max(hi, v); }
  if (!(lo <= hi)) { lo = -5; hi = 5; }
  const ticks = b.dft.ticks.map((t) => t[0]), labels = b.dft.ticks.map((t) => t[1]);
  return {
    data,
    layout: {
      title: { text: `Wannier model vs DFT${ch ? ` (spin ${ch})` : ""}`, x: 0, xanchor: "left", font: { size: 14 } },
      xaxis: kAxis(x, ticks, labels), yaxis: { range: [lo - 1, hi + 1], title: { text: "E − E<sub>F</sub> (eV)" }, zeroline: false, gridcolor: GRID },
      shapes: [...(b.windows?.frozen ? windowShapes({ frozen: b.windows.frozen } as unknown as Windows) : []), ...nodes(ticks), ef],
      legend: { orientation: "h", x: 1, xanchor: "right", y: 1.1 }, margin: { l: 60, r: 20, t: 55, b: 40 },
    },
  };
}

/** Results: the largest |H(R)| against |R| (log scale) with the 1 meV line - how local the model is. */
export function decayFigure(c: WannierChannel) {
  const d = c.decay;
  return {
    data: [{ x: d.distance ?? [], y: (d.max_hopping ?? []).map((h) => Math.max(h * 1000, 1e-4)), type: "scatter", mode: "markers",
      marker: { size: 5, color: "#2a78d6" }, hovertemplate: "|R| = %{x:.2f} Å<br>max |H| = %{y:.3g} meV<extra></extra>" }],
    layout: {
      title: { text: "Decay of the hoppings", x: 0, xanchor: "left", font: { size: 14 } },
      xaxis: { title: { text: "|R| (Å)" }, gridcolor: GRID, zeroline: false },
      yaxis: { title: { text: "max |H<sub>mn</sub>(R)| (meV)" }, type: "log", gridcolor: GRID },
      shapes: [{ type: "line", xref: "x domain", yref: "y", x0: 0, x1: 1, y0: (d.cutoff ?? 0.001) * 1000, y1: (d.cutoff ?? 0.001) * 1000,
        line: { color: ORANGE, width: 1, dash: "dash" } }],
      margin: { l: 70, r: 20, t: 45, b: 45 },
    },
  };
}

export const fmtMeV = (e: number | null | undefined) => (e == null ? "–" : `${(e * 1000).toFixed(e < 0.01 ? 2 : 1)} meV`);

/** "Wannierise this run" (DFT): a new Wannier functions project set up with the run as its source. */
export async function wannieriseRun(dftProjectName: string, dftJob: string, label: string): Promise<Project> {
  const name = `${dftProjectName} (Wannier)`.slice(0, 120);
  const p = await post<Project>("/projects", { product: "wannier", name });
  const mem: WannierMemory = { ...defaultWannierMemory(seedFromName(dftProjectName)), source: "dft", dft_run: dftJob, dft_label: label };
  await patch(`/projects/${p.id}`, { settings: { wannier: mem } });
  return p;
}

/** A drag of a window on the plot (plotly_relayout: "shapes[0].y0" … - shape 0 the outer window, 1 the frozen one) as
 * the new windows, the edges rounded to 0.01 eV; null when the update moved nothing of them. */
export function draggedWindows(w: Windows, update: Record<string, unknown>): Windows | null {
  const out: Windows = { outer: [...w.outer] as [number, number], ...(w.frozen ? { frozen: [...w.frozen] as [number, number] } : {}) };
  let moved = false;
  for (const [k, v] of Object.entries(update)) {
    const m = /^shapes\[(\d)\]\.y([01])$/.exec(k);
    if (!m || typeof v !== "number") continue;
    const which = m[1] === "0" ? "outer" : m[1] === "1" && out.frozen ? "frozen" : null;
    if (!which) continue;
    out[which]![Number(m[2])] = Math.round(v * 100) / 100;
    moved = true;
  }
  if (!moved) return null;
  for (const k of ["outer", "frozen"] as const) { const r = out[k]; if (r && r[0] > r[1]) out[k] = [r[1], r[0]]; }
  return out;
}

/** The live spread of a running job, from its progress message ("localisation: iteration 40, spread 14.2"). */
export function liveSpreadPoint(message: string | undefined): { phase: "dis" | "loc"; iter: number; value: number } | null {
  const m = /^(disentanglement|localisation): iteration (\d+), (?:spread|Omega_I) ([-+0-9.eE]+)/.exec(message ?? "");
  return m ? { phase: m[1] === "disentanglement" ? "dis" : "loc", iter: Number(m[2]), value: Number(m[3]) } : null;
}

/** Ω_I during the disentanglement and Ω during the localisation against the iteration (finished run or live). */
export function convergenceFigure(h: { disentanglement: (number | null)[][]; localisation: (number | null)[][] }, title = "Convergence") {
  const tr = (rows: (number | null)[][], name: string, color: string) => ({ x: rows.map((r) => r[0]), y: rows.map((r) => r[1]),
    type: "scatter", mode: rows.length > 60 ? "lines" : "lines+markers", name, line: { color, width: 2 }, marker: { size: 4 },
    hovertemplate: `${name}<br>iteration %{x}: %{y:.5f} Å²<extra></extra>` });
  return {
    data: [...(h.disentanglement.length ? [tr(h.disentanglement, "Ω_I (disentanglement)", ORANGE)] : []),
      ...(h.localisation.length ? [tr(h.localisation, "Ω (localisation)", "#2a78d6")] : [])],
    layout: {
      title: { text: title, x: 0, xanchor: "left", font: { size: 14 } },
      xaxis: { title: { text: "iteration" }, gridcolor: GRID, zeroline: false },
      yaxis: { title: { text: "spread (Å²)" }, gridcolor: GRID, zeroline: false },
      legend: { orientation: "h", x: 1, xanchor: "right", y: 1.12 }, margin: { l: 60, r: 20, t: 55, b: 45 },
    },
  };
}
